import { useState } from "react";
import { Sparkles, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, authHeaders } from "@/lib/api";
import { toast } from "sonner";

const render = (text) =>
  text.split("\n").map((line, i) => {
    const t = line.trim();
    if (!t) return <div key={i} className="h-2" />;
    if (t.startsWith("### ") || t.startsWith("## ") || t.startsWith("# ")) return <h4 key={i} className="font-heading font-semibold text-slate-900 mt-3 text-sm">{t.replace(/^#+\s*/, "")}</h4>;
    const bold = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, j) => (p.startsWith("**") ? <strong key={j} className="text-slate-900">{p.slice(2, -2)}</strong> : p));
    if (/^[-*•]\s/.test(t)) return <li key={i} className="ml-4 list-disc text-slate-700">{bold(t.replace(/^[-*•]\s/, ""))}</li>;
    return <p key={i} className="text-slate-700">{bold(t)}</p>;
  });

export const NarrativePanel = ({ projectId, initial, modelLabel, canEdit = true }) => {
  const [text, setText] = useState(initial || "");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    setText("");
    try {
      const res = await fetch(`${API}/projects/${projectId}/narrative/stream`, { headers: authHeaders(), credentials: "include" });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed to start analysis");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data: /, "");
          if (line === "[DONE]") continue;
          try {
            const obj = JSON.parse(line);
            if (obj.t) setText((prev) => prev + obj.t);
            if (obj.error) toast.error(obj.error);
          } catch (e) { /* ignore */ }
        }
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 flex flex-col" data-testid="narrative-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-900 inline-flex items-center gap-2"><Sparkles size={16} className="text-emerald-600" /> AI Analysis</h3>
          <p className="text-[11px] text-slate-500">{modelLabel} · narrative on UMR, inflation, manning and GOP drivers</p>
        </div>
        {canEdit && <Button size="sm" variant={text ? "outline" : "default"} onClick={run} disabled={busy} data-testid="generate-narrative-button" className={text ? "" : "bg-emerald-700 hover:bg-emerald-800"}>
          {busy ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <Sparkles size={14} className="mr-1" />} {busy ? "Writing..." : text ? "Regenerate" : "Generate Narrative"}
        </Button>}
      </div>
      <div className="mt-4 text-sm leading-relaxed max-h-[520px] overflow-auto pr-1" data-testid="narrative-text">
        {text ? render(text) : <p className="text-slate-400 text-sm">Click "Generate Narrative" to get an explanation of the budget, the impact of each assumption, risks and recommendations.</p>}
        {busy && <span className="inline-block w-2 h-4 bg-emerald-600 animate-pulse ml-1 align-middle" />}
      </div>
    </div>
  );
};
